// Styles
import './SelectGroup.scss';

// Libs
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';

// Constants
import { SELECT_GROUP_TYPES } from '@/common/constants';
// Assets
import { ReactComponent as ArrowIcon } from '@/materiaux/assets/icons/triangle-bottom.svg';

// Blocks
import Categories from './Categories/Categories';
import {
  SelectGroupDefaultProps,
  SelectGroupPropTypes,
} from './SelectGroup.proptypes';

const SelectGroup = ({
  className,
  context,
  disabled,
  id,
  label,
  name,
  onChange,
  options,
  placeholder,
  readOnly,
  required,
  type,
  value,
}) => {
  const { t } = useTranslation();

  const selectRef = useRef();

  const [isOpen, setIsOpen] = useState(false);
  const [labelInput, setLabelInput] = useState(null);

  /**
   * Check if disabled select wishlist
   * @type {boolean}
   */
  const isDisabledSelect = useMemo(
    () => disabled || options.wishlist_full_count === 0,
    [disabled, options],
  );

  /**
   * Return the state label or placeholder
   * @type {string}
   */
  const getLabel = useMemo(() => labelInput || placeholder, [
    labelInput,
    placeholder,
  ]);

  /**
   * Generate classNames
   * @type {string}
   */
  const classNames = useMemo(
    () =>
      [
        'select-group',
        required ? 'select-group--required' : null,
        disabled || readOnly ? 'select-group--disabled' : null,
        isOpen && !disabled && !readOnly ? 'select-group--open' : null,
        type === SELECT_GROUP_TYPES.DISABLE_EMPTY_ITEMS
          ? 'select-group--disable-empty-items'
          : null,
        className,
      ]
        .filter((val) => val)
        .join(' '),
    [className, disabled, isOpen, readOnly, required, type],
  );

  const handleClickOutside = useCallback((e) => {
    if (selectRef.current && selectRef.current.contains(e.target)) {
      return;
    }
    setIsOpen(false);
  }, []);

  const toggleSelect = useCallback(() => {
    setIsOpen(!isOpen);
  }, [isOpen]);

  const handleKeyPress = useCallback(
    (e) => {
      if (e.code === 'Enter') {
        setIsOpen(!isOpen);
      }
    },
    [isOpen],
  );

  const handleChange = useCallback(
    (e, title) => {
      setIsOpen(false);
      setLabelInput(title);
      onChange(e);
    },
    [onChange],
  );

  useEffect(() => {
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    } else {
      document.removeEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [handleClickOutside, isOpen]);

  return (
    <div
      className={classNames}
      data-qa-id={context || 'select-group-field'}
      ref={selectRef}
      role="button"
      tabIndex="0"
    >
      {label && <span className="select-group__label">{label}</span>}
      <button
        aria-disabled={disabled}
        className="select-group__head"
        data-qa-id={`${context || 'select-group-btn'}-btn-${
          disabled ? 'disabled' : 'active'
        }`}
        disabled={isDisabledSelect}
        id={id}
        name={name}
        onClick={toggleSelect}
        onKeyPress={handleKeyPress}
        type="button"
      >
        <span className="select-group__value">{getLabel}</span>
        <ArrowIcon className="select-group__arrow" />
      </button>
      <ul
        aria-activedescendant={value}
        className="select-group__list"
        data-qa-id="select-group-list"
        role="listbox"
        tabIndex="0"
      >
        {options.orphan_wishlists.length > 0 && (
          <li className="select-group__item" data-qa-id="select-group-item">
            <Categories
              disabled={disabled}
              name={name}
              onChange={handleChange}
              title={t('ebat:entities:wishlists.title')}
              // TODO: to rename
              wishlists={options.orphan_wishlists}
            />
          </li>
        )}
        {options.categories.map((category) => (
          <Fragment key={`select-group-item-${category.id}`}>
            {(category.wishlists.length > 0 ||
              category.children.length > 0) && (
              <li className="select-group__item" data-qa-id="select-group-item">
                {category.wishlists.length > 0 && (
                  <Categories
                    disabled={disabled}
                    name={name}
                    onChange={handleChange}
                    title={category.name}
                    // TODO: to rename
                    wishlists={category.wishlists}
                  />
                )}
                {category.children.length > 0 && (
                  <ul
                    className="select-group__sublist"
                    data-qa-id="select-group-sublist"
                  >
                    {category.children.map(
                      (children) =>
                        children.wishlists?.length > 0 && (
                          <li
                            className="select-group__item"
                            data-qa-id="select-group-item"
                            key={`select-group-item-${children.id}`}
                          >
                            <Categories
                              disabled={disabled}
                              name={name}
                              onChange={handleChange}
                              title={children.name}
                              // TODO: to rename
                              wishlists={children.wishlists}
                            />
                          </li>
                        ),
                    )}
                  </ul>
                )}
              </li>
            )}
          </Fragment>
        ))}
      </ul>
    </div>
  );
};

SelectGroup.defaultProps = {
  ...SelectGroupDefaultProps,
};

SelectGroup.propTypes = {
  ...SelectGroupPropTypes,
};

export default SelectGroup;
